/**
 * Coercers for `validateSearch`. Drawer and modal state lives in search params.
 *
 * TanStack Router's default search parser runs `JSON.parse` on each raw value,
 * so `?code=339827` arrives as the NUMBER 339827 and `?doShowAll=true` as the
 * BOOLEAN true. A `typeof value === "string"` guard therefore silently drops
 * every all-digit value — a join code, a numeric round id — and re-serializes
 * the URL without it. That is a real bug class, not a hypothetical, so every
 * route in this app coerces through these helpers and never type-guards a raw
 * search value itself.
 */

/** Any non-empty scalar, rendered back to a string. */
export function coerceOptionalString(value: unknown): string | undefined {
  if (value === null || value === undefined) return undefined;
  if (typeof value === "string") return value === "" ? undefined : value;
  if (typeof value === "number") {
    return Number.isFinite(value) ? String(value) : undefined;
  }
  if (typeof value === "boolean") return String(value);
  return undefined;
}

/** A string that must be one of a fixed set; anything else falls back. */
export function coerceEnum<const TAllowed extends readonly string[]>(
  value: unknown,
  allowed: TAllowed,
  fallback: TAllowed[number],
): TAllowed[number] {
  const asString = coerceOptionalString(value);
  if (asString === undefined) return fallback;
  return (allowed as readonly string[]).includes(asString)
    ? asString
    : fallback;
}

/** A string that must be one of a fixed set; anything else drops out of the URL. */
export function coerceOptionalEnum<const TAllowed extends readonly string[]>(
  value: unknown,
  allowed: TAllowed,
): TAllowed[number] | undefined {
  const asString = coerceOptionalString(value);
  if (asString === undefined) return undefined;
  return (allowed as readonly string[]).includes(asString)
    ? asString
    : undefined;
}

/** A finite number, whether it arrived already parsed or still as text. */
export function coerceOptionalNumber(value: unknown): number | undefined {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : undefined;
  }
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}

/** `?doShowAll=true` — present-and-true only; everything else is false. */
export function coerceBoolean(value: unknown): boolean {
  if (typeof value === "boolean") return value;
  return coerceOptionalString(value) === "true";
}

/**
 * Reads a param under its current name, falling back to a retired one.
 *
 * Renaming a query key requires aliasing the old name,
 * because a link that is already out in the world — a push notification target,
 * a bookmark, a URL pasted into a support thread — has to keep resolving.
 * Aliases are removed only when the links carrying them can no longer exist.
 */
export function coerceAliasedString(
  search: Record<string, unknown>,
  currentKey: string,
  ...retiredKeys: string[]
): string | undefined {
  const current = coerceOptionalString(search[currentKey]);
  if (current !== undefined) return current;
  for (const key of retiredKeys) {
    const legacy = coerceOptionalString(search[key]);
    if (legacy !== undefined) return legacy;
  }
  return undefined;
}
